/**
 * File Upload Dialog Component
 * Beautiful drag-and-drop interface for uploading project photos and documents
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Paper,
  Grid,
  Fade,
  Zoom,
  Stack
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Photo,
  Description,
  Videocam,
  CheckCircle,
  Error as ErrorIcon,
  InsertDriveFile
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

const FileUploadDialog = ({ 
  open, 
  onClose, 
  projectId, 
  milestones = [], 
  onUploadComplete,
  title = 'Upload Progress Files',
  subtitle = 'Add photos, videos, or documents to track project progress'
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const anyFileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Photo color="primary" />;
    if (file.type.startsWith('video/')) return <Videocam color="secondary" />;
    if (file.type === 'application/pdf') return <Description color="error" />;
    return <InsertDriveFile color="action" />;
  };

  const getFileCategory = (file) => {
    if (file.type.startsWith('image/')) return 'Photo';
    if (file.type.startsWith('video/')) return 'Video';
    if (file.type === 'application/pdf' || file.type.includes('document')) return 'Document';
    return 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'video/mp4',
      'video/quicktime',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: `${file.name} is too large (max 10MB)` };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: `${file.name} has an unsupported file type` };
    }

    return { valid: true };
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addFiles(files);
    event.target.value = '';
  };

  const addFiles = (files) => {
    setError('');
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openFilePicker = (inputRef) => {
    if (!uploading) {
      inputRef.current?.click();
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    }
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      const formData = new FormData();
      
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      if (selectedMilestone) {
        formData.append('milestoneId', selectedMilestone);
      }
      
      if (description) {
        formData.append('description', description);
      }

      const response = await api.post(
        `/projects/${projectId}/files`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onUploadComplete();
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFiles([]);
      setDescription('');
      setSelectedMilestone('');
      setUploadProgress(0);
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUpload color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* Success Message */}
        {success && (
          <Zoom in={success}>
            <Alert 
              severity="success" 
              icon={<CheckCircle />}
              sx={{ mb: 2 }}
            >
              Files uploaded successfully!
            </Alert>
          </Zoom>
        )}

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError('')}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Milestone Selection */}
        <TextField
          select
          fullWidth
          label="Associate with Milestone (Optional)"
          value={selectedMilestone}
          onChange={(e) => setSelectedMilestone(e.target.value)}
          sx={{ mb: 2 }}
          disabled={uploading}
        >
          <MenuItem value="">
            <em>None - General Project Files</em>
          </MenuItem>
          {milestones.map(milestone => (
            <MenuItem key={milestone.id} value={milestone.id}>
              {milestone.order}. {milestone.title}
            </MenuItem>
          ))}
        </TextField>

        {/* Description */}
        <TextField
          fullWidth
          label="Description (Optional)"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a note about these files..."
          sx={{ mb: 3 }}
          disabled={uploading}
        />

        {/* Drag and Drop Area */}
        <input
          ref={anyFileInputRef}
          type="file"
          hidden
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <input
          ref={photoInputRef}
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <input
          ref={videoInputRef}
          type="file"
          hidden
          multiple
          accept="video/*"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <input
          ref={documentInputRef}
          type="file"
          hidden
          multiple
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileSelect}
          disabled={uploading}
        />

        <Paper
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => openFilePicker(anyFileInputRef)}
          role="button"
          tabIndex={uploading ? -1 : 0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFilePicker(anyFileInputRef);
            }
          }}
          sx={{
            p: 4,
            mb: 2,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            bgcolor: dragActive ? 'action.hover' : 'background.paper',
            transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: 'action.hover'
            }
          }}
        >
          <CloudUpload 
            sx={{ 
              fontSize: 48, 
              color: dragActive ? 'primary.main' : 'action.active',
              mb: 1
            }} 
          />
          
          <Typography variant="h6" gutterBottom>
            {dragActive ? 'Drop files here' : 'Drag & drop files here'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            or click to browse
          </Typography>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Max 10MB per file • Up to 10 files at once
          </Typography>
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Photo />}
            onClick={() => openFilePicker(photoInputRef)}
            disabled={uploading}
            fullWidth
          >
            Photos
          </Button>
          <Button
            variant="outlined"
            startIcon={<Videocam />}
            onClick={() => openFilePicker(videoInputRef)}
            disabled={uploading}
            fullWidth
          >
            Videos
          </Button>
          <Button
            variant="outlined"
            startIcon={<Description />}
            onClick={() => openFilePicker(documentInputRef)}
            disabled={uploading}
            fullWidth
          >
            Documents
          </Button>
        </Stack>

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <Fade in={true}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Selected Files ({selectedFiles.length})
              </Typography>
              
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {selectedFiles.map((file, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: 'primary.light',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItem>
                      <ListItemIcon>
                        {getFileIcon(file)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                              {file.name}
                            </Typography>
                            <Chip 
                              label={getFileCategory(file)} 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        }
                        secondary={formatFileSize(file.size)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveFile(index)} 
                          disabled={uploading}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </Box>
          </Fade>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Uploading files...
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {uploadProgress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress}
              sx={{ 
                height: 8, 
                borderRadius: 1,
                bgcolor: 'action.hover'
              }}
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={uploading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained"
          disabled={selectedFiles.length === 0 || uploading}
          startIcon={uploading ? null : <CloudUpload />}
          sx={{ 
            minWidth: 150,
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog;
